import { init } from '@fullstory/browser'
import { useEffect } from 'react'
import { logger } from 'utils/logger'

interface UseFullStoryOptions {
  anonymous?: boolean
  orgId?: string | undefined
  sampleRate?: number
  scoped?: boolean
}

/**
 * Hook to initialize and configure FullStory analytics
 *
 * Related environment variables:
 * - FULLSTORY_ORG_ID: Our FullStory org id (not client orgId), deliberately the same across all
 * environments
 * - FULLSTORY_SAMPLE_RATE: Percentage of sessions to record (0-100), if 0, FullStory is disabled
 * @param {Object} options - Configuration options for FullStory
 * @param {boolean} [options.anonymous=false] - When true, enables anonymous tracking mode
 * @param {string} [options.orgId] - FullStory organization ID
 * @param {number} [options.sampleRate=-1] - Percentage of sessions to record (0-100). -1 disables
 * @param {boolean} [options.scoped=false] - When true, enables cross-domain iFrame recording
 * sampling
 * @returns {void}
 */
export default function useFullStory({
  anonymous = false,
  orgId,
  sampleRate = 0,
  scoped = false,
}: UseFullStoryOptions) {
  // useEffect runs only on client side, ensuring FullStory initialization happens in browser
  // For more information, please refer to: https://react.dev/reference/react/useEffect#caveats
  useEffect(() => {
    if (!orgId) {
      logger('error', `FullStory: No FullStory Org ID found`)
      return
    }
    const record = Math.random() * 100 < sampleRate
    if (!record) {
      logger('debug', `FullStory: Not recording sessions`, {
        context: { fullStoryOrgId: orgId, fullStoryRecordSessions: record, scoped },
      })

      return
    }

    init(
      {
        orgId,
        /**
         * When devMode is false, FullStory will record sessions. Recording should only
         * happen on production. Defaults to false.
         */
        devMode: !(record ?? true),
        recordCrossDomainIFrames: scoped,
        recordOnlyThisIFrame: scoped,
      },
      ({ sessionUrl, settings }: { sessionUrl: string; settings: Readonly<object> }) => {
        logger('debug', 'FullStory: FullStory initialized', { context: { sessionUrl, settings } })
      },
    )
  }, [anonymous, orgId, sampleRate, scoped])
}
